import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CustomBreadcrumb } from '@/shared/components/atoms';
import { CustomCommonScreenHeader, CustomTable, CustomTableColumn, ConfirmationModal } from '@/shared/components/molecules';
import { Home, LayoutGrid, Pencil, Trash2, List } from 'lucide-react';
import { useAuthStore } from '@/shared/stores/authStore';
import { useToastStore } from '@/shared/stores/toastStore';
import { departmentApi } from '../api/departmentApi';

interface SubDepartment {
    id: string;
    name: string;
    created_at: string;
}

export const ListSubDepartments = () => {
    const navigate = useNavigate();
    const { departmentId } = useParams<{ departmentId: string }>();
    const [isLoading, setIsLoading] = useState(true);
    const { user } = useAuthStore();
    const { showToast } = useToastStore();
    const schoolSlug = user?.school?.slug || '';

    const [subDepartments, setSubDepartments] = useState<SubDepartment[]>([]);
    const [page, setPage] = useState(1);
    const pageSize = 10;
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [subDeptToDelete, setSubDeptToDelete] = useState<SubDepartment | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        if (schoolSlug && departmentId) {
            fetchSubDepartments();
        }
    }, [schoolSlug, departmentId]);

    const fetchSubDepartments = async () => {
        setIsLoading(true);
        try {
            const response = await departmentApi.getSubDepartments(schoolSlug, departmentId as string);
            if (response.success !== false) {
                const data = Array.isArray(response.data) ? response.data : (Array.isArray(response) ? response : []);
                setSubDepartments(data);
            } else {
                showToast(response.error || 'Failed to load sub-departments', 'error');
            }
        } catch (error) {
            showToast('An unexpected error occurred', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteClick = (subDept: SubDepartment) => {
        setSubDeptToDelete(subDept);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!subDeptToDelete || !schoolSlug) return;

        setIsDeleting(true);
        try {
            const response = await departmentApi.deleteSubDepartment(schoolSlug, subDeptToDelete.id);
            if (response.success) {
                showToast('Sub-department deleted successfully', 'success');
                fetchSubDepartments();
                setIsDeleteModalOpen(false);
            } else {
                showToast(response.error || 'Failed to delete sub-department', 'error');
            }
        } catch (error) {
            showToast('An unexpected error occurred', 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    const totalCount = subDepartments.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

    const paginatedSubDepartments = useMemo(() => {
        const start = (page - 1) * pageSize;
        return subDepartments.slice(start, start + pageSize);
    }, [subDepartments, page, pageSize]);

    const columns: CustomTableColumn<SubDepartment>[] = [
        {
            header: 'Sub-Department Name',
            key: 'name',
            minWidth: '200px',
            render: (type) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        background: 'rgba(99, 102, 241, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                    }}>
                        <List size={16} color="var(--accent-primary)" />
                    </div>
                    <span style={{ fontWeight: '500' }}>{type.name}</span>
                </div>
            )
        },
        {
            header: 'Created At',
            key: 'created_at',
            minWidth: '130px',
            render: (type) => (
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                    {new Date(type.created_at).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                    })}
                </span>
            )
        },
        {
            header: 'Actions',
            key: 'actions',
            align: 'right',
            minWidth: '130px',
            width: '130px',
            render: (type) => (
                <div className="table-action-cell" style={{ justifyContent: 'flex-end' }}>
                    <button
                        className="table-action-btn"
                        onClick={(e) => { e.stopPropagation(); navigate(`/${schoolSlug}/school-admin/departments/${departmentId}/sub-departments/${type.id}/levels`); }}
                        title="View Levels"
                    >
                        <List size={18} />
                    </button>
                    <button
                        className="table-action-btn"
                        onClick={(e) => { e.stopPropagation(); console.log('Edit Sub-Department:', type.id); }}
                        title="Edit Sub-Department"
                    >
                        <Pencil size={18} />
                    </button>
                    <button
                        className="table-action-btn danger"
                        onClick={(e) => { e.stopPropagation(); handleDeleteClick(type); }}
                        title="Delete Sub-Department"
                    >
                        <Trash2 size={18} />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="page-container">
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/', icon: <Home size={14} /> },
                    { label: 'Departments', path: `/${schoolSlug}/school-admin/departments`, icon: <LayoutGrid size={14} /> },
                    { label: 'Sub-Departments', icon: <List size={14} /> }
                ]}
            />
            <CustomCommonScreenHeader
                title="Sub-Departments"
                subtitle="Manage sub-departments/categories for this department."
                icon={<List size={24} />}
                actionLabel="Add Sub-Department"
                onActionClick={() => navigate(`/${schoolSlug}/school-admin/departments/${departmentId}/sub-departments/add`)}
            />

            <div style={{ marginTop: '1.25rem' }}>
                <CustomTable
                    columns={columns}
                    data={paginatedSubDepartments}
                    isLoading={isLoading}
                    isCompact={true}
                    emptyMessage="No sub-departments found"
                    emptyIcon={<List size={32} />}
                    pagination={{
                        currentPage: page,
                        totalPages: totalPages,
                        totalCount: totalCount,
                        pageSize: pageSize,
                        onPageChange: setPage,
                        hasPrevious: page > 1,
                        hasNext: page < totalPages,
                        isLoading: isLoading,
                        itemName: 'sub-departments',
                    }}
                />
            </div>

            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Sub-Department"
                message={`Are you sure you want to delete the sub-department "${subDeptToDelete?.name}"? This action cannot be undone.`}
                confirmLabel="Delete"
                variant="danger"
                isLoading={isDeleting}
            />
        </div>
    );
};
